import dayjs from 'dayjs'
import { isNil, omitBy } from 'lodash'
import * as queryString from 'query-string'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { logEvent } from 'lib/analytics'
import cookie from 'lib/cookie'
import { ROUTE_PATH } from 'lib/paths'
import { getRedirectUrl } from 'lib/router'
import { SNSSignIn, SNSSignInError } from 'lib/_types'

import {
  useLazyRequestSnsTokenQuery,
  useLinkSnsMutation,
  useSnsSignInMutation,
} from 'store/api/yay/user'
import { addNotification } from 'store/slices/notifications'

import usePageModal from 'features/Modal/hooks/usePageModal'

import { MODAL_MODE } from 'components/Modal/config'

type SnsAuthorizationResponse =
  | {
      provider: 'twitter'
      request_token_token: string
      request_token_secret: string
      oauth_verifier: string
    }
  | {
      provider: 'line'
      code: string
    }
  | {
      provider: 'facebook'
      request_token: string
    }
  | {
      provider: 'apple'
      code: string
      id_token: string
    }
  | {
      provider: 'google'
      access_token: string
    }

export const useSNSAuthorization = (
  callback: (sns: SnsAuthorizationResponse) => void
) => {
  const [requestSnsToken] = useLazyRequestSnsTokenQuery()
  const opened = useRef<Window>(null)

  useEffect(() => {
    window.snsAuthorizationCallback = (
      snsAuthorizationResponse: SnsAuthorizationResponse
    ) => {
      opened.current?.close?.()

      callback(snsAuthorizationResponse)
    }

    return () => {
      window.snsAuthorizationCallback = () => {}
    }
  }, [])

  const onLineClick = () => {
    const state = +dayjs()
    cookie.lineTokenState.set(state)

    opened.current = window.open(
      `https://access.line.me/oauth2/v2.1/authorize?${queryString.stringify({
        response_type: 'code',
        client_id: window?.gon?.LINE_CONSUMER_KEY,
        redirect_uri: `${window.location.origin}/login/callback/line`,
        state,
        scope: 'profile',
      })}`
    )
  }

  const onTwitterClick = () => {
    requestSnsToken({}).then((res) => {
      if ('data' in res && res?.data?.request_token_url) {
        cookie.twitterToken.set(res?.data?.request_token)
        cookie.twitterSecretToken.set(res?.data?.request_secret)

        opened.current = window.open(res?.data?.request_token_url)
      }
    })
  }

  const onFacebookClick = () => {
    if (window?.FB) {
      window?.FB.login(async function (response: any) {
        const requestToken = response?.authResponse?.accessToken
        if (requestToken) {
          window.snsAuthorizationCallback({
            provider: 'facebook',
            request_token: requestToken,
          })
        }
      })
    }
  }

  const onAppleClick = async () => {
    if (!isNil(window?.AppleID?.auth)) {
      const state = (+dayjs())?.toString()
      cookie.appleTokenState.set(state)

      window?.AppleID.auth.init({
        clientId: process.env.APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: `${window.location.origin}/login/callback/apple`,
        state,
        usePopup: true,
      })

      try {
        const authRes = await window?.AppleID.auth.signIn()
        if (authRes && authRes.authorization) {
          const { code, id_token } = authRes.authorization

          window.snsAuthorizationCallback({
            provider: 'apple',
            code,
            id_token,
          })
        }
      } catch (e) {
        console.log(e)
      }
    }
  }

  const onGoogleClick = () => {
    const state = +dayjs()
    cookie.googleTokenState.set(state)

    const redirectUri = `${window.location.origin}/login/callback/google`

    opened.current = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${queryString.stringify({
        response_type: 'code',
        state,
        client_id: process.env.GOOGLE_CLIENT_ID,
        redirect_uri: redirectUri,
        scope: 'openid profile email',
      })}`
    )
  }

  const onWorldcoinClick = () => {
    const state = +dayjs()
    cookie.worldcoinTokenState.set(state)

    opened.current = window.open(
      `https://id.worldcoin.org/authorize?${queryString.stringify(
        {
          response_type: 'code',
          client_id: window?.gon?.WORLDCOIN_CLIENT_ID,
          redirect_uri: `${window.location.origin}/login/callback/worldcoin`,
          state,
          scope: 'openid+profile+email',
          nonce: 1,
        },
        { encode: false }
      )}`
    )
  }

  return {
    onLineClick,
    onTwitterClick,
    onFacebookClick,
    onGoogleClick,
    onAppleClick,
    onWorldcoinClick,
  }
}

export const signInResUserExistsCallback = ({
  signInRes,
}: {
  signInRes: SNSSignIn
}) => {
  logEvent.account_log_in({
    type: signInRes?.res?.sns_info?.type,
    from: 'first_view',
  })

  cookie.userId.set(signInRes?.res?.user_id)

  // Traditional route change to reload application, allowing an app to be aware of a new cookies.
  window.location.href = window.decodeURIComponent(
    getRedirectUrl(window.location.href) ?? ROUTE_PATH.HOME
  )
}

export const useSNSAuthentication = () => {
  const { open } = usePageModal()
  const [snsSignIn] = useSnsSignInMutation()

  const snsAuthorization = useSNSAuthorization((snsAuthorizationResponse) => {
    snsSignIn(snsAuthorizationResponse)
      .unwrap()
      .then((signInRes) => {
        if (signInRes?.res?.user_id) {
          signInResUserExistsCallback({ signInRes })
        } else {
          open(MODAL_MODE.SIGN_UP_FORM, {
            snsInfo: JSON.stringify(omitBy(signInRes?.res?.sns_info, isNil)),
          })
        }
      })
      .catch((e: { data: SNSSignInError }) => {
        if (e?.data?.res?.error_code == -31) {
          open(MODAL_MODE.LOGIN, {
            snsAuthorizationResponse: JSON.stringify({
              data: e?.data,
            }),
          })
        }
      })
  })

  return snsAuthorization
}

export const useSNSLink = () => {
  const { t } = useTranslation()
  const [link] = useLinkSnsMutation()
  const dispatch = useDispatch()

  const snsAuthorization = useSNSAuthorization(
    async (snsAuthorizationResponse) => {
      link(snsAuthorizationResponse)
        .unwrap()
        .then(() => {
          dispatch(
            addNotification({
              message: t('links_information.connect_dialog.success'),
            })
          )
        })
        .catch(() => console.log('Failed to link'))
    }
  )

  return snsAuthorization
}
